import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowDownCircle, ArrowUpCircle, Wallet, TrendingUp, FileWarning, PenLine, Check } from "lucide-react";
import WorkloadCard from "../components/WorkloadCard";
import { Button } from "../components/ui/button";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function Dashboard() {
  const { t, fmt, entity } = useApp();
  const [data, setData] = useState(null);
  const [cf, setCf] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/reports/dashboard?entity_id=${entity}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/reports/cashflow?entity_id=${entity}`).then((r) => setCf(r.data)).catch(() => {});
  }, [entity]);

  if (!data) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const kpis = [
    { label: t("dash.revenue"), value: fmt(data.revenue), icon: TrendingUp, testid: "kpi-revenue", cls: "text-primary" },
    { label: t("dash.receivables"), value: fmt(data.receivables), icon: ArrowDownCircle, testid: "kpi-receivables", cls: "text-emerald-500" },
    { label: t("dash.payables"), value: fmt(data.payables), icon: ArrowUpCircle, testid: "kpi-payables", cls: "text-amber-500" },
    { label: t("dash.net"), value: fmt(data.receivables - data.payables), icon: Wallet, testid: "kpi-net", cls: "text-foreground" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("nav.dashboard")}</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.testid} data-testid={k.testid}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{k.label}</CardTitle>
              <k.icon className={`h-4 w-4 ${k.cls}`} />
            </CardHeader>
            <CardContent>
              <div className="font-mono text-2xl font-semibold tracking-tight">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="cash-flow-chart-container">
          <CardHeader><CardTitle>{t("dash.cashflow")}</CardTitle></CardHeader>
          <CardContent className="h-72">
            {cf && cf.series.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cf.series}>
                  <defs>
                    <linearGradient id="in" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="out" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend />
                  <Area type="monotone" dataKey="inflow" name={t("dash.inflow")} stroke="#10B981" fill="url(#in)" strokeWidth={2} />
                  <Area type="monotone" dataKey="outflow" name={t("dash.outflow")} stroke="#EF4444" fill="url(#out)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">{t("common.noData")}</div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
        <WorkloadCard />
        <Card data-testid="upcoming-dues-card">
          <CardHeader><CardTitle>{t("dash.upcoming")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.upcoming.length === 0 && <div className="text-sm text-muted-foreground">{t("common.noData")}</div>}
            {data.upcoming.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`due-item-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.counterparty}</div>
                  <div className="text-xs text-muted-foreground font-mono">{u.number} · {u.due_date}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold">{fmt(u.total - (u.paid_amount || 0))}</div>
                  {u.overdue
                    ? <Badge variant="destructive" className="text-[10px]">{t("dash.overdue")}</Badge>
                    : <Badge variant="secondary" className="text-[10px]">{u.kind === "sale" ? t("inv.sale") : t("inv.purchase")}</Badge>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="expiring-docs-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="h-4 w-4 text-amber-500" />{t("dash.expiringDocs")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.expiring_docs || []).length === 0 && <div className="text-sm text-muted-foreground">{t("common.noData")}</div>}
            {(data.expiring_docs || []).map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`expdoc-item-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.subject_name || d.doc_type}</div>
                  <div className="text-xs text-muted-foreground font-mono">{d.expiry_date}</div>
                </div>
                {d.overdue
                  ? <Badge variant="destructive" className="text-[10px] shrink-0">{t("dash.overdue")}</Badge>
                  : <Badge variant="secondary" className="text-[10px] shrink-0">{d.days_left} {t("dash.daysLeft")}</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="signatures-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenLine className="h-4 w-4 text-primary" />{t("dash.signatures")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.signatures || []).length === 0 && <div className="text-sm text-muted-foreground" data-testid="signatures-empty">{t("dash.noSignatures")}</div>}
            {(data.signatures || []).map((n) => (
              <div key={n.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`signature-item-${n.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{n.filename}</div>
                  <div className="text-xs text-muted-foreground truncate">{n.message}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant={n.type === "docusign_completed" ? "default" : "destructive"} className="text-[10px]">{t(`dash.sig.${n.type}`)}</Badge>
                  <Button size="icon" variant="ghost" className="h-7 w-7" title={t("dash.markRead")} data-testid={`signature-read-${n.id}`}
                    onClick={() => api.post(`/notifications/${n.id}/read`).then(() => setData({ ...data, signatures: data.signatures.filter((x) => x.id !== n.id) }))}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}
